import { useState } from 'react';
import { useParams } from "react-router";
import { useDropzone } from 'react-dropzone';
import moment from 'moment';
import XLSX from 'xlsx-js-style';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, Chip, Alert, RadioGroup, FormControlLabel, Radio, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, } from '@mui/material';
import { PageLoader } from '../../shared_elements';
import { globalPostExportService } from '../../utils/globalApiServices';
import { downloadFileType, numberWithCommas } from '../../utils';
import { sampleExportApi } from '../reserve_rate/apiService';
import { fieldDateFormat } from '../../constants';

const MAX_FILE_SIZE_MB = 5;
const MAX_ROWS = 200;
const MAX_COLS = 50;
const SUPPORTED_EXTENSIONS = ['.csv', '.xlsx'];
// Matches a cell that is empty, whitespace, or just a currency symbol (what the Export button
// writes for an unpopulated rate, e.g. "$ ") — anything else with no digits (e.g. "abc", "N/A")
// is actual invalid content and must be flagged, not silently treated as blank.
const BLANK_CELL_REGEX = /^[\s$€£¥₹,]*$/;
const BODY_CELL_STYLE = { padding: '6px 16px', borderBottom: '1px solid rgba(224,224,224,1)', fontSize: '0.8125rem', whiteSpace: 'nowrap', textAlign: 'left' };
const STICKY_BODY_CELL_STYLE = { ...BODY_CELL_STYLE, position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 };
const NEW_BADGE_STYLE = { marginLeft: 6, fontSize: '0.65rem', fontWeight: 700, color: '#1e7e34', background: '#e6f4ea', borderRadius: 4, padding: '1px 6px' };

const parsePercent = (header) => {
  if (header === undefined || header === null) return null;
  const str = String(header).trim().replace('%', '');
  if (str === '' || isNaN(Number(str))) return null;
  const num = Number(str);
  if (num < 0 || num > 100) return null;
  return num;
};

const ImportRatioDerateModal = ({ ratioDerateData, onToggleModal, onConfirmImport, handleItemClicked }) => {
  const params = useParams()
  const [fileName, setFileName] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState('append');
  const [isLoading, setLoading] = useState(false);
  const interval_condition = ratioDerateData?.interval_condition || [];
  const existingColumns = interval_condition.map((r) => Number(r.derate));
  const existingRatios = (interval_condition[0]?.ratios || []).map((r) => Number(r.ratio));

  const resetPreview = () => { setPreview(null); setGlobalError(''); setFileName(''); };

  const handleDownloadSample = () => {
    setLoading(true)
    globalPostExportService(sampleExportApi(params?.leaseSlug), { download: "xlsx", reserve_rate_based_on: 11, })
      .then(response => {
        setLoading(false)
        if (response) { downloadFileType(response?.data, `Sample_DeRate_Based_Ratio_Table_${moment().format(fieldDateFormat)}.`, "xlsx"); }
      });
  }

  const processRows = (rows, displayRows) => {
    if (!rows || rows.length === 0) { setGlobalError('No data rows found in the file.'); return; }
    // The sample template includes instructional text above the actual table (title, numbered steps, etc.),
    // so scan for the row whose first cell is the "Ratio / DeRate" header instead of assuming row 0.
    const headerRowIndex = rows.findIndex((r) => String((r || [])[0] ?? '').trim().toLowerCase() === 'ratio / derate');
    if (headerRowIndex === -1) { setGlobalError('Invalid template: could not find a column header row reading "Ratio / DeRate".'); return; }
    // The DeRate % header cells are read from displayRows (formatted display text, e.g. "5%") rather
    // than the raw underlying value — a header cell formatted as an Excel Percentage stores 0.05 for
    // a displayed "5%", so reading the raw number would misparse the column as 0.05%. Ratio/DeRate row
    // values and rate cells intentionally use the raw value (rows) instead: whatever number the user
    // entered (e.g. 0.05 or 5) is what should show in the preview, with no percent-based reinterpretation.
    const header = (displayRows[headerRowIndex] || rows[headerRowIndex]) || [];

    const colDefs = [];
    const rejected = [];
    for (let c = 1; c < header.length; c++) {
      const headerTrimmed = String(header[c] ?? '').trim();
      if (headerTrimmed === '') continue;
      const pct = parsePercent(header[c]);
      if (pct !== null) { colDefs.push({ index: c, derate: pct }); continue; }
      rejected.push({ detail: `Column "${headerTrimmed}"`, reason: 'Column header is not a valid DeRate % — column rejected, its cell values are not imported.' });
    }
    if (colDefs.length === 0) { setGlobalError('Invalid template: at least one DeRate % column is required.'); return; }
    if (colDefs.length > MAX_COLS) { setGlobalError(`File has ${colDefs.length} DeRate % columns, which exceeds the ${MAX_COLS} column limit. Please split the file.`); return; }

    // Blank rows (no cell populated at all) are skipped rather than treated as the end of the table —
    // a blank row does not mean the data below it should be dropped.
    const dataRows = rows.slice(headerRowIndex + 1).filter((r) => (r || []).some((cell) => String(cell ?? '').trim() !== ''));
    if (dataRows.length === 0) { setGlobalError('No data rows found in the file.'); return; }
    if (dataRows.length > MAX_ROWS) { setGlobalError(`File has ${dataRows.length} Ratio/DeRate rows, which exceeds the ${MAX_ROWS} row limit. Please split the file.`); return; }

    const seenRatios = new Set();
    const parsedRows = [];
    dataRows.forEach((r, idx) => {
      const rawRatio = r[0];
      if (rawRatio === undefined || String(rawRatio).trim() === '') return;
      const rawRatioTrimmed = String(rawRatio).trim();
      // Ratio/DeRate is read from the raw underlying value (not display-formatted text), so whatever
      // number the user entered — 0.05 or 5 — is exactly what's parsed and shown, with no percent-based
      // reinterpretation. Only commas/whitespace (thousands-separator formatting) are stripped.
      const ratioNum = Number(rawRatioTrimmed.replace(/[\s,]/g, ''));
      const ratioStr = isNaN(ratioNum) ? rawRatioTrimmed : String(ratioNum);
      if (isNaN(ratioNum)) {
        // A row whose "ratio" is non-numeric text and has no rate cells populated either is not a
        // data row at all — it's incidental text (e.g. a trailing template note) and is ignored
        // silently rather than flagged as a rejected row.
        const hasAnyRate = colDefs.some((cd) => String(r[cd.index] ?? '').trim() !== '');
        if (!hasAnyRate) return;
        rejected.push({ detail: `Row ${idx + 2} ("${ratioStr}")`, reason: 'Ratio/DeRate value is not numeric — row rejected.' });
        return;
      }
      if (seenRatios.has(ratioNum)) { rejected.push({ detail: `Row ${idx + 2} ("${ratioStr}")`, reason: 'Duplicate Ratio/DeRate value in file — first occurrence kept.' }); return; }
      seenRatios.add(ratioNum);
      const cells = {};
      colDefs.forEach((cd) => {
        const raw = r[cd.index];
        const rawTrimmed = String(raw ?? '').trim();
        if (raw === undefined || rawTrimmed === '' || BLANK_CELL_REGEX.test(rawTrimmed)) { cells[cd.derate] = ''; return; }
        // The Export button prefixes a populated cell with the currency symbol (e.g. "$ 123.45") —
        // strip only currency/whitespace/comma formatting before parsing, so a re-imported export
        // round-trips correctly. Letters or other non-numeric content must NOT be stripped away —
        // a value like "abc123" is alphanumeric, not a rate, and must be rejected.
        const stripped = rawTrimmed.replace(/[\s$€£¥₹,]/g, '');
        const num = Number(stripped);
        if (stripped === '' || isNaN(num) || num < 0) {
          rejected.push({ detail: `Row ${idx + 2}, ${cd.derate}% column`, reason: 'Rate is not numeric or is negative — cell left blank, row still imported.' });
          cells[cd.derate] = '';
        } else {
          cells[cd.derate] = String(num);
        }
      });
      parsedRows.push({ ratio: ratioNum, ratioStr, cells });
    });

    if (parsedRows.length === 0) { setGlobalError('No valid data rows found in the file.'); return; }

    const newColumns = colDefs.filter((cd) => !existingColumns.includes(cd.derate));
    const matchedColumns = colDefs.filter((cd) => existingColumns.includes(cd.derate));
    const newRows = parsedRows.filter((pr) => !existingRatios.includes(pr.ratio));
    let updatedCells = 0;
    parsedRows.forEach((pr) => {
      if (!existingRatios.includes(pr.ratio)) return;
      matchedColumns.forEach((cd) => { if (pr.cells[cd.derate] !== '') updatedCells += 1; });
    });

    setPreview({
      colDefs,
      parsedRows,
      rejected,
      summary: { newColumns: newColumns.length, newRows: newRows.length, updatedCells, rejectedCount: rejected.length },
    });
  };

  const onDrop = (acceptedFiles, rejectedFiles) => {
    resetPreview();
    const file = (acceptedFiles?.[0]) || (rejectedFiles?.[0]?.file);
    if (!file) { setGlobalError('Unsupported file type. Please upload a .csv or .xlsx file.'); return; }
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!SUPPORTED_EXTENSIONS.includes(ext)) { setGlobalError('Unsupported file type. Please upload a .csv or .xlsx file.'); return; }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) { setGlobalError(`File exceeds the ${MAX_FILE_SIZE_MB}MB limit.`); return; }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary', cellStyles: false });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        // rows: raw underlying cell values, used for Ratio/DeRate row values and rate cells — the
        // literal number the user entered, with no percent-format reinterpretation.
        // displayRows: each cell's formatted display text (e.g. "5%") instead of its raw underlying
        // value, used ONLY for the DeRate % header row — a header cell formatted as an Excel
        // Percentage stores 0.05 for a displayed "5%", so reading the raw number would misparse the
        // column as 0.05%. Both use identical options (aside from raw) so blank-row filtering lines
        // up 1:1 between the two arrays, keeping row indices in sync.
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: false });
        const displayRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: false, raw: false });
        processRows(rows, displayRows);
      } catch (err) {
        setGlobalError('Unable to read this file. Please confirm it is not corrupt.');
      }
    };
    reader.onerror = () => setGlobalError('Unable to read this file. Please confirm it is not corrupt.');
    reader.readAsBinaryString(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: false,
    accept: { 'text/csv': ['.csv'], 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
  });

  const removeFile = () => resetPreview();

  const handleConfirm = () => {
    if (!preview) return;
    onConfirmImport({ colDefs: preview.colDefs, parsedRows: preview.parsedRows, mode });
    handleItemClicked?.('Confirm Import Button');
    onToggleModal();
  };

  const stickyFirstCol = (extra = {}) => ({ position: 'sticky', left: 0, backgroundColor: '#fff', whiteSpace: 'nowrap', ...extra });
  return (
    <Dialog open={true} maxWidth="md" fullWidth>
      <DialogTitle className="dialog-header">Import Ratio/DeRate Table</DialogTitle>
      <DialogContent dividers sx={{ overflow: 'hidden' }}>
        <Box
          {...getRootProps()}
          sx={{ border: '2px dashed #ccc', borderRadius: '8px', p: 3, textAlign: 'center', cursor: 'pointer', backgroundColor: isDragActive ? '#f0f0f0' : '#fafafa' }}
        >
          <input {...getInputProps()} />
          <Typography fontSize="13px">Drag and drop, or click here to upload a .csv or .xlsx file</Typography>
        </Box>
        {globalError && <Alert severity="error" sx={{ mt: 1 }}>{globalError}</Alert>}
        {fileName && !globalError && (
          <Box sx={{ mt: 2 }}>
            <Chip label={fileName} onDelete={removeFile} variant="outlined" />
          </Box>
        )}
        <Typography fontSize="11px" sx={{ background: '#ffff96', mt: 1, display: 'inline-block' }}>
          Note: Supported (.csv, .xlsx), only 1 file allowed, max {MAX_FILE_SIZE_MB}MB, {MAX_ROWS} rows / {MAX_COLS} DeRate % columns.
        </Typography>
        <Box>
          <Button onClick={() => { handleDownloadSample(); handleItemClicked?.('Download Sample Button') }} sx={{ fontSize: "13px", textTransform: "none", p: 0, minWidth: "auto", color: "primary.main", textDecoration: "underline", "&:hover": { textDecoration: "underline", backgroundColor: "transparent", }, }} > Download Sample </Button>
        </Box>
        {isLoading ? <PageLoader /> : null}

        {preview && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2">
              {preview.summary.newRows} new row(s), {preview.summary.newColumns} new column(s), {preview.summary.updatedCells} cell(s) to update
              {preview.summary.rejectedCount ? `, ${preview.summary.rejectedCount} item(s) rejected` : ''}.
            </Typography>

            <Typography sx={{ mt: 1 }} fontSize="13px">Commit mode:</Typography>
            <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
              <FormControlLabel value="append" control={<Radio size="small" />} label="Append (add to existing table)" />
              <FormControlLabel value="replace" control={<Radio size="small" />} label="Replace (discard existing table)" />
            </RadioGroup>
            <TableContainer component={Paper} sx={{ maxHeight: 'min(220px, calc(100vh - 420px))', overflow: 'auto', mt: 1, mb: 1 }}>
              <Table size="small" stickyHeader sx={{ minWidth: 480 + preview.colDefs.length * 100 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={stickyFirstCol({ zIndex: 3 })}><b>Ratio / DeRate</b></TableCell>
                    {preview.colDefs.map((cd) => (
                      <TableCell key={cd.derate} sx={{ whiteSpace: 'nowrap' }}>
                        <b>{cd.derate}%</b>{!existingColumns.includes(cd.derate) ? <span style={NEW_BADGE_STYLE}>new</span> : null}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.parsedRows.map((pr) => (
                    <tr key={pr.ratio}>
                      <td style={STICKY_BODY_CELL_STYLE}>
                        {pr.ratioStr}{!existingRatios.includes(pr.ratio) ? <span style={NEW_BADGE_STYLE}>new</span> : null}
                      </td>
                      {preview.colDefs.map((cd) => (
                        <td key={cd.derate} style={BODY_CELL_STYLE}>{pr.cells[cd.derate] ? `${ratioDerateData?.currency?.symbol || '$'} ${numberWithCommas(Number(pr.cells[cd.derate]).toFixed(2))}` : '—'}</td>
                      ))}
                    </tr>
                  ))}
                </TableBody>
              </Table>
              {preview.rejected.length > 0 && (
                <Box sx={{ p: 2, borderTop: '1px solid #f0c4c4' }}>
                  <Typography color="error" fontSize="13px" fontWeight="bold">Rejected items ({preview.rejected.length}):</Typography>
                  {preview.rejected.map((r, i) => (
                    <Typography key={i} color="error" fontSize="12px">{r.detail}: {r.reason}</Typography>
                  ))}
                </Box>
              )}
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ mr: 2 }}>
        <Button variant="outlined" size="small" onClick={() => { onToggleModal(); handleItemClicked?.('Cancel Button'); }}>Cancel</Button>
        <Button variant="contained" size="small" disabled={!preview} onClick={handleConfirm}>Confirm Import</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportRatioDerateModal;
